/**
 * AWS X-Ray Browser Tracing Utility
 * This provides custom tracing for browser applications
 * since AWS X-Ray SDK doesn't have native browser support
 */

interface XRaySubsegment {
  id: string;
  name: string;
  start_time: number;
  end_time: number;
  fault?: boolean;
  http?: {
    request: {
      method?: string;
      url: string;
      traced?: boolean;
    };
    response?: {
      status?: number;
    };
  };
  metadata?: Record<string, unknown>;
  cause?: {
    exceptions: Array<{
      message: string;
      type: string;
      stack?: string;
    }>;
  };
}

interface XRaySegment {
  id: string;
  name: string;
  trace_id: string;
  start_time: number;
  end_time?: number;
  service?: {
    name: string;
  };
  fault?: boolean;
  metadata?: Record<string, unknown>;
  cause?: {
    exceptions: Array<{
      message: string;
      type: string;
      stack?: string;
    }>;
  };
  subsegments: XRaySubsegment[];
}

interface PerformanceResourceTiming extends PerformanceEntry {
  initiatorType: string;
  transferSize: number;
  encodedBodySize: number;
  decodedBodySize: number;
}

interface PerformanceNavigationTimingExtended extends PerformanceNavigationTiming {
  redirectCount: number;
}

class XRayBrowserTracer {
  private enabled: boolean;
  private tracingEndpoint: string;
  private serviceName: string;
  private traces: XRaySegment[];
  private currentSegment: XRaySegment | null;
  private traceId: string;

  constructor() {
    this.enabled = import.meta.env.VITE_AWS_XRAY_ENABLED === 'true';
    this.tracingEndpoint = import.meta.env.VITE_AWS_XRAY_ENDPOINT || '/api/traces';
    this.serviceName = 'golf-frontend';
    this.traces = [];
    this.currentSegment = null;
    this.traceId = '';

    if (this.enabled) {
      this.initializeTracing();
    }
  }

  private initializeTracing(): void {
    // Generate a trace ID for this session
    this.traceId = this.generateTraceId();

    // Setup performance observer for resource timing
    if (window.PerformanceObserver) {
      this.setupPerformanceObserver();
    }

    // Setup error tracking
    this.setupErrorTracking();

    // Setup fetch interceptor
    this.setupFetchInterceptor();

    // Send traces periodically
    setInterval(() => this.flushTraces(), 30000); // Every 30 seconds

    // Send traces on page unload
    window.addEventListener('beforeunload', () => this.flushTraces());
  }

  private generateTraceId(): string {
    const version = '1';
    const time = Math.floor(Date.now() / 1000).toString(16);
    const random = Array(24)
      .fill(0)
      .map(() => Math.floor(Math.random() * 16).toString(16))
      .join('');
    return `${version}-${time}-${random}`;
  }

  private generateSegmentId(): string {
    return Array(16)
      .fill(0)
      .map(() => Math.floor(Math.random() * 16).toString(16))
      .join('');
  }

  startSegment(name: string, metadata: Record<string, unknown> = {}): string | null {
    if (!this.enabled) return null;

    const segment: XRaySegment = {
      id: this.generateSegmentId(),
      name,
      trace_id: this.traceId,
      start_time: Date.now() / 1000,
      service: {
        name: this.serviceName
      },
      metadata: {
        ...metadata,
        user_agent: navigator.userAgent,
        url: window.location.href
      },
      subsegments: []
    };

    this.currentSegment = segment;
    return segment.id;
  }

  endSegment(segmentId: string | null, metadata: Record<string, unknown> = {}): void {
    if (!this.enabled || !this.currentSegment || !segmentId) return;

    if (this.currentSegment.id === segmentId) {
      this.currentSegment.end_time = Date.now() / 1000;
      this.currentSegment.metadata = {
        ...this.currentSegment.metadata,
        ...metadata
      };

      this.traces.push(this.currentSegment);
      this.currentSegment = null;
    }
  }

  addSubsegment(name: string, duration: number, metadata: Record<string, unknown> = {}): void {
    if (!this.enabled || !this.currentSegment) return;

    const subsegment: XRaySubsegment = {
      id: this.generateSegmentId(),
      name,
      start_time: (Date.now() - duration) / 1000,
      end_time: Date.now() / 1000,
      metadata
    };

    this.currentSegment.subsegments.push(subsegment);
  }

  private setupPerformanceObserver(): void {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'resource') {
          this.trackResourceTiming(entry as PerformanceResourceTiming);
        } else if (entry.entryType === 'navigation') {
          this.trackNavigationTiming(entry as PerformanceNavigationTimingExtended);
        }
      }
    });

    observer.observe({ entryTypes: ['resource', 'navigation'] });
  }

  private trackResourceTiming(entry: PerformanceResourceTiming): void {
    if (!this.currentSegment) return;

    const subsegment: XRaySubsegment = {
      id: this.generateSegmentId(),
      name: `Resource: ${entry.name}`,
      start_time: entry.startTime / 1000,
      end_time: (entry.startTime + entry.duration) / 1000,
      http: {
        request: {
          url: entry.name,
          method: 'GET'
        },
        response: {
          status: entry.transferSize > 0 ? 200 : 304
        }
      },
      metadata: {
        resource_type: entry.initiatorType,
        transfer_size: entry.transferSize,
        encoded_body_size: entry.encodedBodySize,
        decoded_body_size: entry.decodedBodySize
      }
    };

    if (this.currentSegment) {
      this.currentSegment.subsegments.push(subsegment);
    }
  }

  private trackNavigationTiming(entry: PerformanceNavigationTimingExtended): void {
    const metadata = {
      dom_interactive: entry.domInteractive,
      dom_content_loaded: entry.domContentLoadedEventEnd,
      dom_complete: entry.domComplete,
      load_event_end: entry.loadEventEnd,
      type: entry.type.toString(),
      redirect_count: entry.redirectCount
    };

    this.addSubsegment('Page Load', entry.loadEventEnd, metadata);
  }

  private setupErrorTracking(): void {
    window.addEventListener('error', (event) => {
      this.trackError(event.error, 'window.error');
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.trackError(event.reason, 'unhandledrejection');
    });
  }

  private trackError(error: Error | unknown, source: string): void {
    if (!this.enabled) return;

    const err = error as Error;

    const errorSegment: XRaySegment = {
      id: this.generateSegmentId(),
      name: `Error: ${err?.message || 'Unknown error'}`,
      trace_id: this.traceId,
      start_time: Date.now() / 1000,
      end_time: Date.now() / 1000,
      fault: true,
      cause: {
        exceptions: [{
          message: err?.message || 'Unknown error',
          type: err?.name || 'Error',
          stack: err?.stack || ''
        }]
      },
      metadata: {
        source,
        url: window.location.href,
        user_agent: navigator.userAgent
      },
      subsegments: []
    };

    this.traces.push(errorSegment);
  }

  private setupFetchInterceptor(): void {
    const originalFetch = window.fetch;

    window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
      const startTime = Date.now();
      const [resource, init] = args;
      const method = init?.method || 'GET';
      const url = typeof resource === 'string' ? resource : (resource instanceof Request ? resource.url : resource.toString());

      const subsegmentId = this.generateSegmentId();

      try {
        const response = await originalFetch(...args);
        const duration = Date.now() - startTime;

        if (this.currentSegment) {
          this.currentSegment.subsegments.push({
            id: subsegmentId,
            name: `HTTP ${method} ${url}`,
            start_time: startTime / 1000,
            end_time: (startTime + duration) / 1000,
            http: {
              request: {
                method,
                url,
                traced: true
              },
              response: {
                status: response.status
              }
            }
          });
        }

        return response;
      } catch (error) {
        const duration = Date.now() - startTime;
        const err = error as Error;

        if (this.currentSegment) {
          this.currentSegment.subsegments.push({
            id: subsegmentId,
            name: `HTTP ${method} ${url}`,
            start_time: startTime / 1000,
            end_time: (startTime + duration) / 1000,
            fault: true,
            http: {
              request: {
                method,
                url,
                traced: true
              }
            },
            cause: {
              exceptions: [{
                message: err.message,
                type: err.name
              }]
            }
          });
        }

        throw error;
      }
    };
  }

  private async flushTraces(): Promise<void> {
    if (!this.enabled || this.traces.length === 0) return;

    const tracesToSend = [...this.traces];
    this.traces = [];

    try {
      await fetch(this.tracingEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Amzn-Trace-Id': `Root=${this.traceId}`
        },
        body: JSON.stringify({
          traces: tracesToSend,
          service: this.serviceName,
          timestamp: Date.now()
        })
      });
    } catch (error) {
      console.error('Failed to send traces:', error);
      // Re-add traces if sending failed
      this.traces = [...tracesToSend, ...this.traces];
    }
  }

  // Public API for manual tracing
  trace<T>(name: string, fn: () => T, metadata: Record<string, unknown> = {}): T | Promise<T> {
    if (!this.enabled) return fn();

    const segmentId = this.startSegment(name, metadata);

    try {
      const result = fn();

      if (result instanceof Promise) {
        return result
          .then((value) => {
            this.endSegment(segmentId, { success: true });
            return value;
          })
          .catch((error) => {
            this.endSegment(segmentId, {
              success: false,
              error: (error as Error).message
            });
            throw error;
          });
      }

      this.endSegment(segmentId, { success: true });
      return result;
    } catch (error) {
      this.endSegment(segmentId, {
        success: false,
        error: (error as Error).message
      });
      throw error;
    }
  }
}

// Create and export singleton instance
const xrayTracer = new XRayBrowserTracer();
export default xrayTracer;
